import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { serverLogoutUser, getAccessToken } from '@/stores/auth/services';
import { localLogoutUser } from '@/api/services/user';
import { useNavigation } from '@react-navigation/native';
import { Platform } from 'react-native';
import { useStoreActions, useStoreState } from '@/stores/store';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import { constants } from '@/config/constants';
import { Text } from '@/components/ui/therapist/Text';

interface TherapistDemoContextType {
  isDemoLogoutVisible: boolean;
  toggleDemoLogout: () => void;
  removeDemoLogoutModel: () => void;
  confirmActionForEndDemo: () => Promise<void>;
}

const TherapistDemoContext = createContext<
  TherapistDemoContextType | undefined
>(undefined);

interface TherapistDemoProviderProps {
  children: ReactNode;
  popupDelayMs?: number;
}

export function TherapistDemoProvider({
  children,
  popupDelayMs = constants.popupDelayMs,
}: TherapistDemoProviderProps) {
  const { navigate } = useNavigation();
  const [isDemoLogoutVisible, setIsDemoLogoutVisible] = useState(false);
  const { isTherapistDemo, toolTipNumber } = useStoreState((store) => ({
    isTherapistDemo: store.isTherapistDemo,
    toolTipNumber: store.toolTipNumber,
  }));
  let logoutDemoAfter5mns: number | undefined = undefined;

  const toggleDemoLogout = useCallback(() => {
    setIsDemoLogoutVisible((prev) => !prev);
  }, []);

  const confirmActionForEndDemo = useCallback(async () => {
    try {
      if (Platform.OS === 'web') {
        const url = new URL(window.location.href);
        url.searchParams.delete('demoStarten');
        window.history.replaceState(null, '', url.toString());
      }
      const jwt = await getAccessToken();
      if (jwt) {
        await serverLogoutUser({ jwt });
      }
      await localLogoutUser();
      setIsDemoLogoutVisible(false);
      (navigate as any)('SignUpController', {});
    } catch {
      await localLogoutUser();
    }
  }, [navigate]);

  const removeDemoLogoutModel = useCallback(() => {
    clearTimeout(logoutDemoAfter5mns);
    setIsDemoLogoutVisible(false);
    logoutDemoAfter5mns = setTimeout(() => {
      setIsDemoLogoutVisible(true);
    }, popupDelayMs);
  }, []);

  useEffect(() => {
    if (!isTherapistDemo) {
      return;
    }
    logoutDemoAfter5mns = setTimeout(() => {
      setIsDemoLogoutVisible(true);
    }, popupDelayMs);

    return () => {
      clearTimeout(logoutDemoAfter5mns);
    };
  }, [isTherapistDemo]);

  const value: TherapistDemoContextType = {
    isDemoLogoutVisible,
    toggleDemoLogout,
    removeDemoLogoutModel,
    confirmActionForEndDemo,
  };

  const [toolTipModal, setToolTipModal] = useState({ text: '', show: false });

  useEffect(() => {
    if (isTherapistDemo) {
      if (toolTipNumber === 1) {
        setToolTipModal(() => ({
          show: true,
          text: 'In dieser Demo-Version können Sie die App in Ruhe ausprobieren und sich mit den Funktionen vertraut machen. Bevor Sie mit dem Üben starten können, laden Sie bitte ein Übungsset herunter.',
          header: 'Willkommen in der neolexon Aphasie-App für Therapeut:innen!',
        }));
      }
      if (toolTipNumber === 2) {
        setToolTipModal(() => ({
          text: 'Heruntergeladene Übungen können hier gestartet werden.',
          show: true,
        }));
      }
      if (toolTipNumber === 3) {
        setToolTipModal(() => ({
          text: 'Werfen Sie hier einen Blick in unsere Datenbank. Hier können Sie Übungssets aus unserer umfangreichen Materialdatenbank aus über 10.000 Items (Wörter, Sätze, Texte) erstellen.',
          show: true,
        }));
      }
    }
  }, [toolTipNumber, isTherapistDemo]);

  const { setFocusOnLaden } = useStoreActions((actions) => ({
    setFocusOnLaden: actions.setFocusOnLaden,
  }));

  const removeTheToolTipModal = () => {
    if (toolTipNumber === 1 && Platform.OS !== 'web') {
      setFocusOnLaden(true);
    }
    setToolTipModal((prev) => ({
      ...prev,
      show: false,
    }));
  };

  return (
    <TherapistDemoContext.Provider value={value}>
      {children}
      <Modal
        onClose={removeTheToolTipModal}
        isVisible={toolTipModal.show}
        confirmActionButton={
          <Button label="Weiter" onPress={removeTheToolTipModal} />
        }
      >
        <Text style={{ fontSize: 20 }}>{toolTipModal.text}</Text>
      </Modal>
      <Modal
        isVisible={isDemoLogoutVisible}
        title="Demo beenden"
        confirmActionButton={
          <Button label="Demo beenden" onPress={confirmActionForEndDemo} />
        }
        cancelActionButton={
          <Button
            label="Abbrechen"
            look="secondary"
            onPress={removeDemoLogoutModel}
          />
        }
        onClose={removeDemoLogoutModel}
      >
        <Text style={{ fontSize: 18 }}>
          Danke für das Ausprobieren der Demo!
        </Text>
      </Modal>
    </TherapistDemoContext.Provider>
  );
}

export function useTherapistDemo(): TherapistDemoContextType {
  const context = useContext(TherapistDemoContext);
  if (context === undefined) {
    throw new Error(
      'useTherapistDemo must be used within a TherapistDemoProvider',
    );
  }
  return context;
}
